import { useCallback, useEffect, useRef, useState } from 'react'
import { emptyVideoRefusal } from '@/features/editor/lib/emptyVideoBlocks'
import { designOverflowRefusal } from '@/features/editor/lib/publishIssues'
import { X } from 'lucide-react'
import { Button, cn, TooltipProvider } from '@teach/ui'
import type { CourseWithoutBlocks } from '@/types/course'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setDialogOpen } from '../../editorSlice'
import { useIsCourseAuthor } from '../../hooks/useIsCourseAuthor'
import { useUpdateCourseMutation } from '../../coursesApi'
import { ValidationErrorsDialog } from '../ValidationErrorsDialog'
import { PublishDialogBody } from './PublishDialogBody'
import { useCourseAccess } from './useCourseAccess'
import { EmptyVideoGate, type EmptyVideoGateState } from './EmptyVideoGate'
import { EmptyVideoStatus } from './EmptyVideoStatus'
import {
  ClientIssuesGate,
  clientOnlyIssues,
  type ClientIssuesState,
} from '../course-settings/ClientIssuesGate'
import { useDismissPanel } from './useDismissPanel'
import { PublishSheetBackdrop, PublishSheetHandle } from './PublishSheetChrome'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  course: CourseWithoutBlocks
}

export const PublishDialog = ({ course }: Props) => {
  const m = useMessages(messages)
  const dispatch = useAppDispatch()
  const open = useAppSelector((s) => s.editor.dialogs.publishOpen)
  const isCourseAuthor = useIsCourseAuthor(course)
  const courseAccess = useCourseAccess(course)
  const { validationErrors, clearValidationErrors } = courseAccess
  const [updateCourse] = useUpdateCourseMutation()
  const close = useCallback(
    () => dispatch(setDialogOpen({ dialog: 'publishOpen', open: false })),
    [dispatch],
  )
  useDismissPanel(open, close)

  // Черновик, а не чтение прямо из course: categoryIds — полная замена на бэке, а обновлённый
  // course приходит асинхронно (инвалидация тега), поэтому клик до рефетча иначе считал бы
  // следующий next от устаревшего списка и стирал бы предыдущий выбор.
  const [categoryIds, setCategoryIds] = useState<number[]>(() => course.categories.map((c) => c.id))

  // Гейт публикации (п.19): пока не знаем блоки урока — считаем это блоком,
  // а не пустым списком («не знаем» ≠ «пусто»), поэтому старт — isLoading: true.
  const [videoGate, setVideoGate] = useState<EmptyVideoGateState>({
    isLoading: true,
    isError: false,
    empties: [],
    retry: () => {},
  })
  // Переполнение составного блока api не проверит — его меряют зонды (как во вкладке «Доступ»).
  const [clientGate, setClientGate] = useState<ClientIssuesState>({ pending: true, byLesson: {} })

  // PublishDialog монтируется безусловно (EditorShell) — Radix открывает/закрывает только
  // DialogContent, сам компонент не размонтируется. Поэтому mount-only seed навсегда
  // замораживал черновик и терял правки категорий из CourseSettingsDialog, сделанные, пока
  // этот диалог был закрыт. Ресидим только на переходе closed→open (как useCourseSettingsForm),
  // а НЕ на каждом рефетче курса — иначе рефетч после первого клика затирал бы второй быстрый
  // клик тем же способом, который правился в раунде 1.
  const prevOpenRef = useRef(false)
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setCategoryIds(course.categories.map((c) => c.id))
    }
    // Гейт сбрасывается на ЗАКРЫТИИ, а не на открытии: облачко рендерится сразу, и
    // эффект `EmptyVideoGate` (дочерний — срабатывает раньше родительского) успевал
    // доложить итог до сброса — сброс затирал его, и публикация висела «загружаю».
    if (!open && prevOpenRef.current) {
      setVideoGate({ isLoading: true, isError: false, empties: [], retry: () => {} })
      setClientGate({ pending: true, byLesson: {} })
    }
    prevOpenRef.current = open
  }, [open, course])

  // Гейт запрещает ровно ПОВЫШЕНИЕ открытости (см. `AccessSection`), а не всю
  // секцию доступа: правка разделов и понижение доступа читателей не добавляют.
  // Курс с черновиком правок: доступ меняется у ОДОБРЕННОЙ версии, а гейт
  // пустого видео считает экраны черновика — пустое видео в нём не повод
  // запрещать открыть или закрыть курс (решение заказчика). Что именно уйдёт
  // при повышении доступа, решает и проверяет бэк (отказ — диалог ошибок).
  const ownsDraft = (courseAccess.moderation?.draftId ?? null) !== null
  // Переполнение — то же правило: у курса с черновиком отказа нет, но «ещё меряем» повышение закрывает.
  const publishBlocked =
    (!ownsDraft && (videoGate.isLoading || videoGate.isError)) || clientGate.pending
  const videoRefusal = ownsDraft
    ? null
    : (emptyVideoRefusal(videoGate.empties) ??
      designOverflowRefusal(clientOnlyIssues(course, clientGate.byLesson)))

  // Оптимистичный черновик обязан откатываться: иначе после провалившегося PUT пикер
  // продолжает показывать выбор, которого на сервере нет (тост от middleware не чинит вид).
  const saveCategories = (next: number[]) => {
    const prev = categoryIds
    setCategoryIds(next)
    void updateCourse({ id: course.id, patch: { categoryIds: next } })
      .unwrap()
      .catch(() => setCategoryIds(prev))
  }

  return (
    <>
      {/* Не модальное окно, а облачко под кнопкой «Опубликовать» в углу: курс за ним
          остаётся виден и доступен. Секции не сжимаются (`[&>*]:shrink-0`): flex-колонка
          с капом высоты иначе ужимала карточку отказа (у неё `overflow-hidden`) под окно —
          список нарушений обрезался вместе с подсказкой, а прокручивать было нечего.
          Прокрутка одна — у всего облачка, без полосы. */}
      {open ? (
        <>
          <PublishSheetBackdrop />
          <section
            data-publish-panel
            role="region"
            aria-label={m.publishCourse}
            className={cn(
              'scrollbar-hide fixed z-50 flex flex-col gap-4 overflow-y-auto bg-popover p-5 text-popover-foreground shadow-2xl duration-200 animate-in [&>*]:shrink-0',
              // Телефон — шторка снизу, как остальные настройки; с `sm` — облачко, как было.
              'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-3xl border-t border-border pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] slide-in-from-bottom',
              'sm:inset-x-auto sm:bottom-auto sm:right-3 sm:top-14 sm:max-h-[calc(100dvh-5rem)] sm:w-[420px] sm:max-w-[calc(100vw-1.5rem)] sm:rounded-2xl sm:border sm:pb-5 sm:fade-in sm:slide-in-from-bottom-0 sm:slide-in-from-top-2',
            )}
          >
            <TooltipProvider delayDuration={200}>
              <PublishSheetHandle />
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold">{m.publishCourse}</h2>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  onClick={close}
                  aria-label={m.close}
                >
                  <X className="size-4" />
                </Button>
              </div>

              <EmptyVideoGate lessons={course.lessons} onChange={setVideoGate} />
              <ClientIssuesGate course={course} ready onChange={setClientGate} />
              {ownsDraft ? null : <EmptyVideoStatus gate={videoGate} />}

              <PublishDialogBody
                course={course}
                courseAccess={courseAccess}
                categoryIds={categoryIds}
                onCategoryIdsChange={saveCategories}
                raiseChecking={publishBlocked}
                raiseRefusal={videoRefusal}
                isCourseAuthor={isCourseAuthor}
                onNavigate={close}
              />
            </TooltipProvider>
          </section>
        </>
      ) : null}

      <ValidationErrorsDialog
        open={validationErrors.length > 0}
        errors={validationErrors}
        onClose={clearValidationErrors}
      />
    </>
  )
}
